/**
 * Настройки замка приложения.
 *
 * Отпечаток ПИНа лежит в защищённом хранилище устройства — там же, где пароли
 * от копий и от Диска. Остальное (включён ли замок, через сколько запирать,
 * пускать ли по отпечатку пальца) — обычные настройки: знание о том, что замок
 * есть, ничего не открывает.
 *
 * ## Про задержку
 *
 * Планировщик открывают десять раз в день. Спрашивать код на каждое открытие —
 * верный способ добиться того, чтобы замок выключили в первый же вечер.
 * Поэтому по умолчанию приложение запирается через пятнадцать минут после
 * того, как его свернули: утром один ввод, дальше день работает как обычно.
 */

import { db } from '@/shared/db/database';
import { readSecret, removeSecret, writeSecret } from '@/shared/lib/secret-storage';

import type { PinDigest } from './pin';

export interface LockSettings {
  enabled: boolean;
  /** Через сколько минут в фоне приложение запирается. `0` — сразу. */
  timeoutMin: number;
  /** Открывать отпечатком или лицом, если устройство умеет. */
  biometrics: boolean;
}

export const DEFAULT_LOCK: LockSettings = { enabled: false, timeoutMin: 15, biometrics: true };

/** Варианты задержки — словами, которые человек выбирает, а не числом минут. */
export const TIMEOUT_OPTIONS = [
  { value: 0, label: 'Сразу' },
  { value: 1, label: 'Через минуту' },
  { value: 15, label: 'Через 15 минут' },
  { value: 60, label: 'Через час' },
];

const settingsKey = (userId: string) => `lock:${userId}`;
const pinKey = (userId: string) => `lock.pin:${userId}`;

export async function readLockSettings(userId: string): Promise<LockSettings> {
  const row = await db.settings.get(settingsKey(userId));
  const value = row?.value as Partial<LockSettings> | undefined;

  return {
    enabled: value?.enabled ?? DEFAULT_LOCK.enabled,
    timeoutMin: value?.timeoutMin ?? DEFAULT_LOCK.timeoutMin,
    biometrics: value?.biometrics ?? DEFAULT_LOCK.biometrics,
  };
}

export async function writeLockSettings(
  userId: string,
  patch: Partial<LockSettings>,
): Promise<LockSettings> {
  const next = { ...(await readLockSettings(userId)), ...patch };
  await db.settings.put({ key: settingsKey(userId), value: next, updatedAt: Date.now() });

  return next;
}

export async function readPinDigest(userId: string): Promise<PinDigest | null> {
  const raw = await readSecret(pinKey(userId));
  if (raw === null) return null;

  try {
    const value = JSON.parse(raw) as Partial<PinDigest>;
    if (typeof value.salt !== 'string' || typeof value.hash !== 'string') return null;

    return { salt: value.salt, hash: value.hash, iterations: value.iterations ?? 0 };
  } catch {
    // Испорченный отпечаток — то же, что его отсутствие: замка нет, и человек
    // не должен остаться перед дверью, которую нечем открыть.
    return null;
  }
}

export async function writePinDigest(userId: string, digest: PinDigest): Promise<void> {
  await writeSecret(pinKey(userId), JSON.stringify(digest));
}

/** Снимает замок целиком — вместе с отпечатком ПИНа. */
export async function clearLock(userId: string): Promise<void> {
  await removeSecret(pinKey(userId));
  await writeLockSettings(userId, { enabled: false });
}
